"use client"

import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import useAppContext from "./useAppContext"
import { toast } from "react-toastify"
import type { AppContextType } from "../context/AppContext"
import type { DraftData, DraftItem } from "../static/types/draft-types"
import * as DraftService from "../services/Draft_service"

/**
 * Hook personnalisé pour gérer les brouillons
 * Respecte le principe d'ouverture/fermeture (O de SOLID)
 */
export const useDraftManager = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [currentDraft, setCurrentDraft] = useState<DraftItem | null>(null)
  const navigate = useNavigate()

  const {

    draftFormData,

    setLastTab,
    setDraftFormData,
    setActiveIndex,
    setSelectedType,
    announcementTypeList,
    setAnnouncementTypeList,
    clearContext,

    setCompanyName,
    setCompanyNameValidity,

    setStatusSignature,
    setStatusSignatureValidity,

    setTheSociety,
    setTheSocietyValidity,

    setHeadOfficeAddress,
    setHeadOfficeAddressValidity,

    setManagers,
    setManagersValidity,

    setIndividual,
    setIndividualValidity,
    
    setManagersWithRepresentative,
    setManagersWithRepresentativeValidity,

    setStatutoryAuditorWithRepresentative,
    setStatutoryAuditorWithRepresentativeValidity,
    
    setDeputyAuditorWithRepresentative,
    setDeputyAuditorWithRepresentativeValidity,

    setCouncilMeeting,
    setCouncilMeetingValidity,

    setIdentity,
    setIdentityValidity,

    setAdmission,
    setClause,

    setGeneralManagers,
    setExecutiveOfficers,
    setStatutoryAuditor,
    setAlternateAuditor,
    
    setTypeGeneralManagersData,
    setTypeExecutiveOfficersData,
    setTypeStatutoryAuditorData,
    setTypeAlternateAuditorData,
    setTypeGeneralManagerData,

    setManager,

    setAssociates,

    setDecisionWithQuality,
    setDecisionWithQualityAndEffectiveDate,

    setLiquidationHeadquarter,
    setTypeAddress,

    setResidentialAddress,
    setMainResidenceAddress,
    setAddress,setLocationOppositionsRegistry,
    setValidityOppositionLocation,

    setDate,
    setdateNotary,
    setdatedeces,
    setcontractDate,
    setDeathDate,
    setRegistrationDate,
    setAuditorChangeEffectiveDate,
    setCompanyNameChangeEffectiveDate,
    setDurationChangeEffectiveDate,
    setDurationChangeDate,
    setPresidencyChangeEffectiveDate,
    setSocialObjectChangeEffectiveDate,
    setSocialCapitalChangeEffectiveDate,
    setseatTransfertChangeEffectiveDate,
    setDateOfWill,
    setDateOfFiling,
    setDateOfReceipt,

    setReducedManager,
    setOldSecondAdministrator,
    setFormerStatutoryAuditor,
    setFormerAlternateAuditor,

    setReducedStatutoryAuditor,
    setManagerWithRepresentative,
    setSecondManagerWithRepresentative,

    setTypeOldAdministratorData,
    setTypeSecondManagerWithRepresentativeData,
    setLegalEntity,
    setCompanyNameUpdate,

    setLeaderChange,
    setCheckedIndex,

    setDenomination,
    setTexteAnnonce,
    setClientReference,
    setLegalForm,
    setDateWhithEffectiveDate,
    setFloid,
    setEntete,
    setChoix,

  } = useAppContext() as AppContextType

  /**
   * Sauvegarde un brouillon
   */
  const saveDraft = useCallback(async (data: DraftData) => {
    setLoading(true)
    try {
      console.log("Tentative de sauvegarde via useDraftManager:", data)

      // Vérifier que les données essentielles sont présentes
      if (!data.announcementType) {
        console.error("Type d'annonce manquant dans les données")
        toast.error("Type d'annonce non spécifié")
        return { success: false, message: "Type d'annonce non spécifié" }
      }

      // Vérifier que l'API URL est configurée
      if (!process.env.REACT_APP_API_URL) {
        console.error("REACT_APP_API_URL n'est pas configuré")
        toast.error("Erreur de configuration: URL de l'API manquante")
        return { success: false, message: "Erreur de configuration: URL de l'API manquante" }
      }

      const response = await DraftService.saveDraft(data)
      if (response.success) {
        // toast.success("Brouillon sauvegardé avec succès")
        return response
      } else {
        toast.error(response.message)
        return response
      }
    } catch (error: any) {
      console.error("Erreur dans useDraftManager.saveDraft:", error)
      const errorMessage = error?.message || "Erreur lors de la sauvegarde"
      toast.error(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Charge un brouillon par son ID
   */
  const loadDraft = useCallback(
    async (draftId: string) => {
      
      clearContext()

      setLoading(true)

      try {

        const response = await DraftService.getDraft(draftId)

        if (response.success && response.data) {

          const { activeIndex, activeTab, formData, announcementType, entete, floId, typeEntete} = response.data

          if( entete ){
            setEntete( entete )
          }

          if( floId ){
            setFloid( floId )
          }

          if( typeEntete ){
            setChoix( typeEntete )
          }

          // Convertir la chaîne JSON en objet si c'est une chaîne
          let parsedFormData = formData
          if (typeof formData === "string") {
            try {
              parsedFormData = JSON.parse(formData)
            } catch (e) {
              console.error("Erreur lors du parsing du formData:", e)
              // Continuer avec la chaîne originale si le parsing échoue
            }
          }

          setDraftFormData(typeof formData === "string" ? formData : JSON.stringify(formData))

          // Mettre à jour le contexte avec les données du brouillon

          if( parsedFormData.checkedIndex !== undefined && parsedFormData.checkedIndex !== null ){
  setCheckedIndex( Number( parsedFormData.checkedIndex ))
} else {
  setCheckedIndex(1) // ✅ défaut = assisté, cohérent avec le contexte
} 

          if (parsedFormData.companyName) {
            setCompanyName(parsedFormData.companyName)
            setCompanyNameValidity(true)
          }

          if (parsedFormData.statusSignature) {
            setStatusSignature(parsedFormData.statusSignature)
            setStatusSignatureValidity(true)
          }

          if (parsedFormData.theSociety) {
            setTheSociety(parsedFormData.theSociety)
            setTheSocietyValidity(true)
          }

          if (parsedFormData.headOfficeAddress) {
            setHeadOfficeAddress(parsedFormData.headOfficeAddress)
            setHeadOfficeAddressValidity(true)
          }

          if (parsedFormData.managers) {
            setManagers(parsedFormData.managers)
            setManagersValidity(true)
          }

          if (parsedFormData.individual) {
            setIndividual(parsedFormData.individual)
            setIndividualValidity(true)
          }

          if (parsedFormData.managersWithRepresentative) {
            setManagersWithRepresentative(parsedFormData.managersWithRepresentative)
            setManagersWithRepresentativeValidity(true)
          }

          if (parsedFormData.statutoryAuditorWithRepresentative) {
            setStatutoryAuditorWithRepresentative(parsedFormData.statutoryAuditorWithRepresentative)
            setStatutoryAuditorWithRepresentativeValidity(true)
          }

          if (parsedFormData.deputyAuditorWithRepresentative) {
            setDeputyAuditorWithRepresentative(parsedFormData.deputyAuditorWithRepresentative)
            setDeputyAuditorWithRepresentativeValidity(true)
          }

          if (parsedFormData.councilMeeting) {
            setCouncilMeeting(parsedFormData.councilMeeting)
            setCouncilMeetingValidity(true)
          }

          if (parsedFormData.identity) {
            setIdentity(parsedFormData.identity)
            setIdentityValidity(true)
          }

          if (parsedFormData.admission) {
            setAdmission(parsedFormData.admission)
          }

          if (parsedFormData.clause) {
            setClause(parsedFormData.clause)
          }

          if (parsedFormData.typeGeneralManagersData) {
            setTypeGeneralManagersData(parsedFormData.typeGeneralManagersData)
          }

          if (parsedFormData.generalManagers) {
            setGeneralManagers(parsedFormData.generalManagers)
          }

          if (parsedFormData.typeExecutiveOfficersData) {
            setTypeExecutiveOfficersData(parsedFormData.typeExecutiveOfficersData)
          }

          if (parsedFormData.executiveOfficers) {
            setExecutiveOfficers(parsedFormData.executiveOfficers)
          }

          if (parsedFormData.typeStatutoryAuditorData) {
            setTypeStatutoryAuditorData(parsedFormData.typeStatutoryAuditorData)
          }

          if (parsedFormData.statutoryAuditor) {
            setStatutoryAuditor(parsedFormData.statutoryAuditor)
          }

          if (parsedFormData.typeAlternateAuditorData) {
            setTypeAlternateAuditorData(parsedFormData.typeAlternateAuditorData)
          }

          if (parsedFormData.alternateAuditor) {
            setAlternateAuditor(parsedFormData.alternateAuditor)
          }

          if (parsedFormData.typeGeneralManagerData) {
            setTypeGeneralManagerData(parsedFormData.typeGeneralManagerData)
          }

          if (parsedFormData.manager) {
            setManager(parsedFormData.manager)
          }

          if (parsedFormData.associates) {
            setAssociates(parsedFormData.associates)
          }

          if (parsedFormData.decisionWithQuality) {
            setDecisionWithQuality(parsedFormData.decisionWithQuality)
          }

          if (parsedFormData.decisionWithQualityAndEffectiveDate) {
            setDecisionWithQualityAndEffectiveDate(parsedFormData.decisionWithQualityAndEffectiveDate)
          }

          if (parsedFormData.liquidationHeadquarter) {
            setLiquidationHeadquarter(parsedFormData.liquidationHeadquarter)
          }

          if (parsedFormData.typeAddress) {
            setTypeAddress(parsedFormData.typeAddress)
          }

          if (parsedFormData.residentialAddress) {
            setResidentialAddress(parsedFormData.residentialAddress)
          }

          if (parsedFormData.mainResidenceAddress) {
            setMainResidenceAddress(parsedFormData.mainResidenceAddress)
          }

          if (parsedFormData.address) {
            setAddress(parsedFormData.address)
          }

          if (parsedFormData.locationOppositionsRegistry) {
            setLocationOppositionsRegistry(parsedFormData.locationOppositionsRegistry)
          }

          if (parsedFormData.validityOppositionLocation) {
            setValidityOppositionLocation(parsedFormData.validityOppositionLocation)
          }

          if (parsedFormData.date) {
            setDate(parsedFormData.date)
          }

          if (parsedFormData.dateNotary) {
            setdateNotary(parsedFormData.dateNotary)
          }

          if (parsedFormData.datedeces) {
            setdatedeces(parsedFormData.datedeces)
          }

          if (parsedFormData.contractDate) {
            setcontractDate(parsedFormData.contractDate)
          }

          if (parsedFormData.deathDate) {
            setDeathDate(parsedFormData.deathDate)
          }

          if (parsedFormData.dateWhithEffectiveDate) {
            setDateWhithEffectiveDate(parsedFormData.dateWhithEffectiveDate)
          }

          if (parsedFormData.registrationDate) {
            setRegistrationDate(parsedFormData.registrationDate)
          }

          if (parsedFormData.auditorChangeEffectiveDate) {
            setAuditorChangeEffectiveDate(parsedFormData.auditorChangeEffectiveDate)
          }

          if (parsedFormData.companyNameChangeEffectiveDate) {
            setCompanyNameChangeEffectiveDate(parsedFormData.companyNameChangeEffectiveDate)
          }

          if (parsedFormData.durationChangeEffectiveDate) {
            setDurationChangeEffectiveDate(parsedFormData.durationChangeEffectiveDate)
          }

          if (parsedFormData.durationChangeDate) {
            setDurationChangeDate(parsedFormData.durationChangeDate)
          }

          if (parsedFormData.presidencyChangeEffectiveDate) {
            setPresidencyChangeEffectiveDate(parsedFormData.presidencyChangeEffectiveDate)
          }

          if (parsedFormData.socialObjectChangeEffectiveDate) {
            setSocialObjectChangeEffectiveDate(parsedFormData.socialObjectChangeEffectiveDate)
          }

          if (parsedFormData.socialCapitalChangeEffectiveDate) {
            setSocialCapitalChangeEffectiveDate(parsedFormData.socialCapitalChangeEffectiveDate)
          }

          if (parsedFormData.seatTransfertChangeEffectiveDate) {
            setseatTransfertChangeEffectiveDate(parsedFormData.seatTransfertChangeEffectiveDate)
          }

          if (parsedFormData.dateOfWill) {
            setDateOfWill(parsedFormData.dateOfWill)
          }

          if (parsedFormData.dateOfFiling) {
            setDateOfFiling(parsedFormData.dateOfFiling)
          }

          if (parsedFormData.dateOfReceipt) {
            setDateOfReceipt(parsedFormData.dateOfReceipt)
          }

          if (parsedFormData.reducedManager) {
            setReducedManager(parsedFormData.reducedManager)
          }

          if (parsedFormData.oldSecondAdministrator) {
            setOldSecondAdministrator(parsedFormData.oldSecondAdministrator)
          }

          if (parsedFormData.formerStatutoryAuditor) {
            setFormerStatutoryAuditor(parsedFormData.formerStatutoryAuditor)
          }

          if (parsedFormData.formerAlternateAuditor) {
            setFormerAlternateAuditor(parsedFormData.formerAlternateAuditor)
          }

          if (parsedFormData.reducedStatutoryAuditor) {
            setReducedStatutoryAuditor(parsedFormData.reducedStatutoryAuditor)
          }

          if (parsedFormData.managerWithRepresentative) {
            setManagerWithRepresentative(parsedFormData.managerWithRepresentative)
          }

          if (parsedFormData.secondManagerWithRepresentative) {
            setSecondManagerWithRepresentative(parsedFormData.secondManagerWithRepresentative)
          }

          if (parsedFormData.typeOldAdministratorData) {
            setTypeOldAdministratorData(parsedFormData.typeOldAdministratorData)
          }

          if (parsedFormData.typeSecondManagerWithRepresentativeData) {
            setTypeSecondManagerWithRepresentativeData(parsedFormData.typeSecondManagerWithRepresentativeData)
          }

          if (parsedFormData.legalEntity) {
            setLegalEntity(parsedFormData.legalEntity)
          }

          if (parsedFormData.companyNameUpdate) {
            setCompanyNameUpdate(parsedFormData.companyNameUpdate)
          }

          if (parsedFormData.leaderChange) {
            setLeaderChange(parsedFormData.leaderChange)
          }

          if (parsedFormData.denomination) {
            setDenomination(parsedFormData.denomination)
          }

          if (parsedFormData.legalForm) {
            setLegalForm(parsedFormData.legalForm)
          }

          if (parsedFormData.clientReference) {
            setClientReference(parsedFormData.clientReference)
          }

          if (parsedFormData.texteAnnonce) {
            setTexteAnnonce(parsedFormData.texteAnnonce)
          }

          // Définir l'onglet actif et l'index
          if ( activeTab ) {
            setLastTab(activeTab)
          }

          if ( activeIndex ) {
            setActiveIndex(activeIndex)
          }

          // Définir le type d'annonce
          if (announcementType) {
            const tempType = announcementTypeList.find( item => item.sSType.toString().toLowerCase() === announcementType.toString().toLowerCase())
            setSelectedType( tempType ?? {
              nType: 0, // Valeur par défaut, à ajuster selon votre logique
              sSType: announcementType,
              nSType: 0, // Valeur par défaut, à ajuster selon votre logique
            })
          }
          setActiveIndex(1)
          setCurrentDraft(response.data)

          // Naviguer vers la page appropriée avec les données du brouillon
          if (announcementType) {
            navigateToCreation(announcementType, draftId)
          }

          return true
        } else {
          toast.error(response.message || "Impossible de charger le brouillon")
          return false
        }
      } catch (error) {
        console.error("Erreur lors du chargement du brouillon:", error)
        toast.error("Une erreur est survenue lors du chargement du brouillon")
        return false
      } finally {
        setLoading(false)
      }
    },
    [
      setCompanyName,
      setCompanyNameValidity,
      setStatusSignature,
      setStatusSignatureValidity,
      setTheSociety,
      setTheSocietyValidity,
      setHeadOfficeAddress,
      setHeadOfficeAddressValidity,
      setManagers,
      setManagersValidity,
      setLastTab,
      setDraftFormData,
      setActiveIndex,
      setSelectedType,
    ],
  )

  /**
   * Supprime un brouillon
   */
  const deleteDraft = useCallback(async (draftId: string) => {
    setLoading(true)
    try {
      const response = await DraftService.deleteDraft(draftId)
      if (response.success) {
        // toast.success("Brouillon supprimé avec succès")
        return true
      } else {
        toast.error(response.message)
        return false
      }
    } catch (error) {
      toast.error("Une erreur est survenue lors de la suppression")
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Récupère tous les brouillons
   */
  const fetchDrafts = useCallback(async () => {
    setLoading(true)
    try {
      const response = await DraftService.getDrafts()
      if (response.success && response.data) {
        return response.data
      } else {
        toast.error(response.message || "Impossible de récupérer les brouillons")
        return []
      }
    } catch (error) {
      toast.error("Une erreur est survenue lors de la récupération des brouillons")
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Navigue vers la page de création d'annonce avec le brouillon chargé
   */
  const navigateToCreation = useCallback(
    (announcementType: string, draftId: string, options?: any) => {
      navigate(`/creation/${announcementType.toLowerCase()}`, {
        state: { fromDraft: true, draftId },
      })
    },
    [navigate],
  )
  
  const stringifyDraft = ( data: any ) => { return JSON.stringify( data )}
  const parseDraft: any = () => { return draftFormData ? JSON.parse( draftFormData ) : {} }

  const pushDraftContent = ( fields: { [ key: string ]: any } ) => {
    let draft = parseDraft()
    const tempDraft = { ...draft, ...fields }
    setDraftFormData( stringifyDraft( tempDraft ))
  }

  return {
    loading,
    currentDraft,
    saveDraft,
    loadDraft,
    deleteDraft,
    fetchDrafts,
    navigateToCreation,
    pushDraftContent
  }
}
